import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import Header from '../component/Header';
import SideBar from '../component/SideBar';
import AppLoader from '../utils/AppLoader';
import { doVendorGET } from '../utils/service';
import { END_POINTS } from '../utils/service/EndPoints';
import { msToHMSShort } from '../utils/validation';
import { toast } from 'react-toastify';
import HistoryDateFilter, { ANALYTICS_DATE_PRESETS, buildHistoryDateParams, formatCustomChipLabel } from '../component/HistoryDateFilter';

const formatCallTime = (item) => {
    const raw = item?.createdAt || item?.callStartTime;
    if (raw == null || raw === '') return '-';
    let value = raw;
    if (typeof raw === 'number' || /^\d+$/.test(String(raw))) {
        const ms = Number(raw);
        value = ms < 1e12 ? ms * 1000 : ms;
    }
    const m = moment(value).tz ? moment(value) : moment(value);
    if (!m.isValid()) return '-';
    return m.format('DD MMM YYYY, hh:mm A');
};

const formatRoomLabel = (item) => {
    const name = String(item?.roomName || '').trim();
    const num = item?.roomNumber != null ? String(item.roomNumber).trim() : '';
    if (!name && !num) return 'Room';
    if (!num) return name;
    if (!name) return `Room ${num}`;
    if (name === num || name.includes(num)) return name;
    return `${name} (${num})`;
};

class VendorCallSummary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            datePreset: 'today',
            fromDate: null,
            toDate: null,
            isLoading: false,
            dashboard: null,
            expandedWards: {},
        };
    }

    componentDidMount() {
        this.fetchDashboard();
    }

    fetchDashboard = async (override = {}) => {
        const datePreset = override.datePreset != null ? override.datePreset : this.state.datePreset;
        const fromDate = override.fromDate !== undefined ? override.fromDate : this.state.fromDate;
        const toDate = override.toDate !== undefined ? override.toDate : this.state.toDate;
        this.setState({ isLoading: true });
        try {
            const response = await doVendorGET(END_POINTS.VENDOR_CALL_ANALYTICS, {
                ...buildHistoryDateParams(datePreset, fromDate, toDate),
                limit: 15,
            });
            if (response?.response_code === 0) {
                const dashboard = response.response || {};
                const expandedWards = {};
                (dashboard.wardUsage || []).forEach((ward) => {
                    expandedWards[String(ward.wardId ?? ward.wardName)] = true;
                });
                this.setState({
                    dashboard,
                    datePreset,
                    fromDate,
                    toDate,
                    expandedWards,
                    isLoading: false,
                });
            } else {
                this.setState({ isLoading: false, dashboard: null });
                toast.error(response?.response_message || 'Unable to load Call Summary');
            }
        } catch (error) {
            this.setState({ isLoading: false, dashboard: null });
            toast.error('Unable to load Call Summary');
        }
    };

    onDateFilterChange = (next) => {
        const samePreset = next.datePreset === this.state.datePreset;
        const sameFrom = (next.fromDate || null) === (this.state.fromDate || null);
        const sameTo = (next.toDate || null) === (this.state.toDate || null);
        if (samePreset && sameFrom && sameTo) return;
        this.fetchDashboard(next);
    };

    toggleWard = (wardKey) => {
        this.setState((prev) => ({
            expandedWards: {
                ...prev.expandedWards,
                [wardKey]: !prev.expandedWards[wardKey],
            },
        }));
    };

    sortRooms = (rooms = []) =>
        [...rooms].sort((a, b) => {
            const aTotal = Number(a.totalCount || 0) + Number(a.ongoingCount || 0);
            const bTotal = Number(b.totalCount || 0) + Number(b.ongoingCount || 0);
            if (bTotal !== aTotal) return bTotal - aTotal;
            const an = Number(a.roomNumber);
            const bn = Number(b.roomNumber);
            if (!Number.isNaN(an) && !Number.isNaN(bn) && an !== bn) return an - bn;
            return String(a.roomLabel || a.roomName || '').localeCompare(String(b.roomLabel || b.roomName || ''));
        });

    renderCallRow = (item, key) => (
        <div className={`call-summary-row ${item.isOngoing ? 'is-live' : ''}`} key={key}>
            <div className="call-summary-row-main">
                <div className="call-summary-room">{formatRoomLabel(item)}</div>
                {item.wardName ? <div className="call-summary-meta">{item.wardName}</div> : null}
                <div className="call-summary-meta">{formatCallTime(item)}</div>
            </div>
            <div className="call-summary-row-side">
                <span className={`call-summary-status ${item.isOngoing ? 'live' : (item.isAttended ? 'ok' : 'missed')}`}>
                    {item.statusLabel || (item.isOngoing ? 'Waiting for nurse' : (item.isAttended ? 'Answered' : 'Not answered'))}
                </span>
                {item.callAckMessage ? (
                    <div className="call-summary-meta">Ack: {item.callAckMessage}</div>
                ) : null}
                {!item.isOngoing && Number(item.callDuration) > 0 ? (
                    <div className="call-summary-meta">{msToHMSShort(Number(item.callDuration))}</div>
                ) : null}
            </div>
        </div>
    );

    render() {
        const { datePreset, fromDate, toDate, isLoading, dashboard, expandedWards } = this.state;
        const periodLabel = dashboard?.dateLabel
            || (datePreset === 'custom'
                ? formatCustomChipLabel(fromDate, toDate)
                : (ANALYTICS_DATE_PRESETS.find((f) => f.key === datePreset)?.label || 'Today'));
        const wardUsage = [...(dashboard?.wardUsage || [])].sort((a, b) => {
            const aScore = Number(a.totalCount || 0) + Number(a.ongoingCount || 0);
            const bScore = Number(b.totalCount || 0) + Number(b.ongoingCount || 0);
            if (bScore !== aScore) return bScore - aScore;
            return String(a.wardName || '').localeCompare(String(b.wardName || ''));
        });
        const ongoingCalls = dashboard?.ongoingCalls || [];
        const recentCalls = dashboard?.recentCalls || [];

        return (
            <div className="dashboard-screen-part">
                <div className="d-flex p-0 h-100">
                    <SideBar />
                    <div className="card-right p-0 call-summary-layout">
                        <div className="call-summary-layout-header">
                            <Header />
                        </div>
                        <div className="call-summary-page">
                            <div className="call-summary-header">
                                <h4 className="mb-1">Call Summary</h4>
                                <p className="call-summary-intro mb-0">
                                    Choose a day or period below. Numbers show how many times patients called for help from each ward and room.
                                </p>
                                <div className="call-summary-scope mt-2">All wards</div>
                            </div>

                            <HistoryDateFilter
                                datePreset={datePreset}
                                fromDate={fromDate}
                                toDate={toDate}
                                filters={ANALYTICS_DATE_PRESETS}
                                label="Show calls for"
                                onChange={this.onDateFilterChange}
                            />

                            <div className="call-summary-section-title">Overall · {periodLabel}</div>
                            <div className="call-summary-stats">
                                <div className="call-summary-stat">
                                    <div className="value">{Number(dashboard?.totalCount || 0)}</div>
                                    <div className="label">Total calls</div>
                                </div>
                                <div className="call-summary-stat answered">
                                    <div className="value">{Number(dashboard?.attendedCount || 0)}</div>
                                    <div className="label">Answered</div>
                                </div>
                                <div className="call-summary-stat missed">
                                    <div className="value">{Number(dashboard?.missedCount || 0)}</div>
                                    <div className="label">Not answered</div>
                                </div>
                            </div>

                            {Number(dashboard?.ongoingCount || 0) > 0 ? (
                                <div className="call-summary-now-banner">
                                    <strong>{Number(dashboard?.ongoingCount || 0)}</strong>
                                    <span>Happening now</span>
                                </div>
                            ) : null}

                            <div className="call-summary-section-title">Calls by ward and room</div>
                            <p className="call-summary-hint">
                                Tap a ward to see each room. Higher numbers mean patients used the call button more.
                            </p>

                            {wardUsage.length === 0 && !isLoading ? (
                                <div className="call-summary-empty">No wards or rooms found</div>
                            ) : (
                                wardUsage.map((ward) => {
                                    const key = String(ward.wardId ?? ward.wardName);
                                    const expanded = expandedWards[key] !== false;
                                    const rooms = this.sortRooms(ward.rooms || []);
                                    return (
                                        <div className="call-summary-ward" key={`ward-${key}`}>
                                            <button
                                                type="button"
                                                className="call-summary-ward-header"
                                                onClick={() => this.toggleWard(key)}
                                            >
                                                <div className="ward-left">
                                                    <div className="ward-title">{ward.wardName || 'Ward'}</div>
                                                    <div className="ward-sub">
                                                        {Number(ward.roomCount || rooms.length)} rooms · {expanded ? 'Tap to hide rooms' : 'Tap to see rooms'}
                                                    </div>
                                                </div>
                                                <div className="ward-counts">
                                                    <span><b>{Number(ward.totalCount || 0)}</b> Total</span>
                                                    <span className="ok"><b>{Number(ward.attendedCount || 0)}</b> Answered</span>
                                                    <span className="missed"><b>{Number(ward.missedCount || 0)}</b> Missed</span>
                                                    <span className="live"><b>{Number(ward.ongoingCount || 0)}</b> Now</span>
                                                </div>
                                                <span className="ward-chevron">{expanded ? '▾' : '▸'}</span>
                                            </button>
                                            {expanded ? (
                                                rooms.length === 0 ? (
                                                    <div className="call-summary-empty px-3 py-2">No rooms added in this ward</div>
                                                ) : (
                                                    <div className="call-summary-table-wrap">
                                                        <div className="call-summary-table-head">
                                                            <div className="col-room">Room</div>
                                                            <div className="col-num">Answered</div>
                                                            <div className="col-num">Missed</div>
                                                            <div className="col-num">Now</div>
                                                        </div>
                                                        {rooms.map((room, idx) => {
                                                            const answered = Number(room.attendedCount || 0);
                                                            const missed = Number(room.missedCount || 0);
                                                            const now = Number(room.ongoingCount || 0);
                                                            const idle = answered + missed === 0 && now === 0;
                                                            return (
                                                                <div
                                                                    className={`call-summary-table-row ${now > 0 ? 'is-live' : ''}`}
                                                                    key={`room-${room.srmIpRoomId ?? idx}`}
                                                                >
                                                                    <div className="col-room">
                                                                        <div className="room-name">{room.roomLabel || room.roomName || 'Room'}</div>
                                                                        {idle ? <div className="room-idle">No calls yet</div> : null}
                                                                    </div>
                                                                    <div className="col-num ok">{answered}</div>
                                                                    <div className={`col-num ${missed > 0 ? 'missed' : 'muted'}`}>{missed}</div>
                                                                    <div className={`col-num ${now > 0 ? 'live' : 'muted'}`}>{now}</div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )
                                            ) : null}
                                        </div>
                                    );
                                })
                            )}

                            <div className="call-summary-section-title">Calls happening now</div>
                            {ongoingCalls.length === 0 ? (
                                <div className="call-summary-empty">No patient is calling right now</div>
                            ) : (
                                <div className="call-summary-list">
                                    {ongoingCalls.map((item, index) => this.renderCallRow(item, `ongoing-${item.id ?? index}`))}
                                </div>
                            )}

                            <div className="call-summary-section-title">Last few calls · {periodLabel}</div>
                            {recentCalls.length === 0 ? (
                                <div className="call-summary-empty">No calls in this period</div>
                            ) : (
                                <div className="call-summary-list">
                                    {recentCalls.map((item, index) => this.renderCallRow(item, `recent-${item.id ?? index}`))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {isLoading ? <AppLoader /> : null}
            </div>
        );
    }
}

export default withRouter(VendorCallSummary);
